import { useToast } from "@/components/podkit/toasts/use-toast";
import { useSegmentTrack } from "@/hooks/use-segment";
import { useEditorURL, type PlainEditor } from "@/queries/editor-queries";
import { useGetOrganizationPolicies } from "@/queries/organization-queries";
import { isValidURL } from "@/utils/url-validation";
import { type PlainMessage } from "@bufbuild/protobuf";
import { type Environment } from "gitpod-next-api/gitpod/v1/environment_pb";
import { useCallback } from "react";
import { useDesktop } from "@/hooks/use-desktop";
import { appMainService } from "@/ipc";

export function useOpenEditor() {
    const editorURL = useEditorURL();
    const { isDesktop } = useDesktop();
    const segmentTrack = useSegmentTrack();
    const { data: organizationPolicies } = useGetOrganizationPolicies();
    const { toast } = useToast();

    const track = useCallback(
        (editor: PlainEditor) => {
            segmentTrack("Open Editor", {
                editor_id: editor.id,
                editor_alias: editor.alias,
                editor_name: editor.name,
            });
        },
        [segmentTrack],
    );

    return useCallback(
        async (editor: PlainEditor, environment?: Environment | PlainMessage<Environment>, version?: string) => {
            try {
                let newWindow: WindowProxy | null = null;
                // opening "about:blank" does not work in desktop. There, we use shell.openExternal instead.
                if (isValidURL(editor.urlTemplate) && !isDesktop) {
                    const url = new URL(editor.urlTemplate);
                    if (url.protocol === "https:") {
                        // We open the window with about:blank first and only later set the real URL once we have it.
                        // This is because if we try to open the window after fetching our editor URL, we risk being
                        // too late and the browser blocking the popup.
                        newWindow = window.open("about:blank", "_blank");
                    }
                }

                if (!organizationPolicies) {
                    return;
                }

                if (organizationPolicies.portSharingDisabled && editor.alias === "vscode-browser") {
                    toast({
                        title: "Port sharing required for VS Code Browser",
                        description:
                            "VS Code Browser requires port sharing to be enabled in your organization policies.",
                    });
                    return;
                }

                const result = await editorURL.mutateAsync({ editorId: editor.id, environment, version });
                const url = result.url;
                if (!url) {
                    return;
                }

                if (isDesktop && appMainService) {
                    await appMainService.openExternal({ url });
                    return;
                }

                if (!newWindow) {
                    // Create hidden iframe as using window.location.href
                    // kills vscode websocket connection
                    const iframe = document.createElement("iframe");
                    iframe.src = url;
                    iframe.style.display = "none";
                    document.body.appendChild(iframe);
                    setTimeout(() => document.body.removeChild(iframe), 2000);
                } else {
                    newWindow.location.href = url;
                }
                track(editor);
            } catch (e) {
                console.error(e);
            }
        },
        [isDesktop, organizationPolicies, editorURL, track, toast],
    );
}
